import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Camera, Flame, History, Plus, TrendingUp, Trophy, Handshake } from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";
import { format } from "date-fns";
import type { DashboardViewProps } from "@/ui/view-props";
import { UNIT_VALUE } from "@/lib/score";
import { PlayerAvatar } from "@/components/PlayerAvatar";
import { CountUp } from "@/components/ui/count-up";

export function DashboardClassicView({
  stats,
  recentSettledSession,
  hotPlayers,
  allTimeStats,
  unsettledStats,
  unsettledExpenses,
  loading,
  settings,
  isReadOnly,
  targetUserId,
  activeSessionId,
  onAbortSession,
}: DashboardViewProps) {

  const navigate = useNavigate();

  const handlePlayerClick = (playerId: string) => {
    const path = isReadOnly
      ? `/public/${targetUserId}/player/${playerId}`
      : `/player/${playerId}`;
    navigate(path);
  };

  return (
    <div className="py-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
          <Trophy className="h-5 w-5 text-primary-foreground" />
        </div>
        <div>
          <h1 className="text-xl font-bold tracking-tight">SSC Score Tracker</h1>
          <p className="text-sm text-muted-foreground">{isReadOnly ? 'Public View' : 'Track your sessions'}</p>
        </div>
      </div>

      {activeSessionId && !isReadOnly && (
        <Card className="border-primary/50 bg-primary/5 shadow-md">
          <CardContent className="p-4 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Flame className="h-5 w-5 text-primary animate-pulse" />
              </div>
              <div>
                <p className="font-bold">Active Session</p>
                <p className="text-xs text-muted-foreground">You have a session in progress</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="text-xs text-muted-foreground"
                onClick={() => {
                  const url = `${window.location.origin}/live/${activeSessionId}`;
                  navigator.clipboard.writeText(url).then(() => alert('Live link copied! Share it with spectators.'));
                }}
              >
                Live Link
              </Button>
              <Button size="sm" variant="destructive" onClick={onAbortSession}>Abort</Button>
              <Link to="/session/new">
                <Button size="sm" className="font-bold">Resume</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3">
        {!isReadOnly && (
          <>
            <Link to="/session/new">
              <Button className="w-full h-16 text-base font-bold flex-col gap-1" size="lg">
                <Plus className="h-5 w-5" />
                New Session
              </Button>
            </Link>
            <Link to="/session/import">
              <Button
                variant="outline"
                className="w-full h-16 text-base font-bold flex-col gap-1 border-2"
              >
                <Camera className="h-5 w-5" />
                Import
              </Button>
            </Link>
          </>
        )}
        <Link to={isReadOnly ? `/public/${targetUserId}/sessions` : "/sessions"}>
          <Button variant="secondary" className="w-full h-16 flex-col gap-1 font-bold">
            <History className="h-5 w-5" />
            Sessions
          </Button>
        </Link>
        <Link to={isReadOnly ? `/public/${targetUserId}/settlement` : "/settlement"}>
          <Button variant="secondary" className="w-full h-16 flex-col gap-1 font-bold">
            <Handshake className="h-5 w-5" />
            {isReadOnly ? 'Settlement' : 'Settle Up'}
          </Button>
        </Link>
      </div>

      {settings.recent_results && !loading && recentSettledSession && (
        <div className="space-y-3">
          <h2 className="text-lg font-bold tracking-tight">Recent Results</h2>
          <Link to={isReadOnly ? `/public/${targetUserId}/session/${recentSettledSession.id}` : `/session/${recentSettledSession.id}`}>
            <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <p className="text-sm font-semibold text-muted-foreground">
                    {format(new Date(recentSettledSession.date), "MMM d, yyyy")}
                  </p>
                  <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">
                    Settled
                  </span>
                </div>
                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                      Top Winner
                    </p>
                    {(() => {
                      const topWinner = [...recentSettledSession.players].sort(
                        (a, b) => b.net - a.net
                      )[0];
                      return (
                        <p className="font-bold text-lg">
                          {topWinner?.name || "N/A"}
                        </p>
                      );
                    })()}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                      Total Pot
                    </p>
                    <p className="font-mono font-bold text-lg">
                      {formatCurrency(recentSettledSession.players.reduce(
                        (sum, p) => sum + (p.buyIns * (recentSettledSession.buyInUnit ?? UNIT_VALUE)),
                        0
                      ))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      )}

      {settings.whos_hot && !loading && hotPlayers.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-orange-500 fill-orange-500" />
            <h2 className="text-lg font-bold tracking-tight">Who's Hot?</h2>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {hotPlayers.map((player, i) => (
              <Card
                key={i}
                className="border-0 shadow-sm min-w-[140px] bg-gradient-to-br from-orange-50 to-white dark:from-orange-950/20 dark:to-background"
              >
                <CardContent className="p-4 flex flex-col items-center text-center">
                  <p className="font-bold text-sm mb-1">{player.name}</p>
                  <div className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
                    <TrendingUp className="h-3 w-3" />
                    <p className="text-xs font-bold uppercase tracking-wider">
                      {player.streak} Streak
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {!loading && unsettledStats.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold tracking-tight">Current Unsettled Standings</h2>
            <Link 
              to={isReadOnly ? `/public/${targetUserId}/who-pays-who` : "/who-pays-who"} 
              className="text-sm text-primary font-semibold hover:underline"
            >
              Who Pays Who →
            </Link>
          </div>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-1.5 font-medium text-muted-foreground">
                        Player
                      </th>
                      <th className="text-right py-1.5 font-medium text-muted-foreground">
                        Sess
                      </th>
                      <th className="text-right py-1.5 font-medium text-muted-foreground">
                        Spent
                      </th>
                      <th className="text-right py-1.5 font-medium text-muted-foreground">
                        Net
                      </th>
                      </tr>
                      </thead>
                      <tbody>
                      {unsettledStats.map((p, i) => (
                      <tr
                        key={i}
                        className="border-b border-border/50 last:border-0 hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => handlePlayerClick(p.id)}
                      >
                        <td className="py-1.5 font-medium">
                          <div className="flex items-center gap-2">
                            <PlayerAvatar name={p.name} avatarUrl={p.avatar_url} size={24} />
                            {p.name}
                          </div>
                        </td>
                        <td className="py-1.5 text-right font-mono text-xs text-muted-foreground">
                          {p.totalSessions}
                        </td>
                        <td className="py-1.5 text-right font-mono text-xs text-muted-foreground">
                          {formatCurrency(p.totalSpent)}
                        </td>
                        <td
                          className={cn(
                            "py-1.5 text-right font-mono font-bold",
                            p.totalNet > 0
                              ? "text-success"
                              : p.totalNet < 0
                                ? "text-destructive"
                                : "text-muted-foreground"
                          )}
                        >
                          {p.totalNet >= 0 ? "+" : ""}
                          <CountUp end={p.totalNet} formatter={formatCurrency} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {!loading && unsettledExpenses.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold tracking-tight">Unsettled Expenses</h2>
            <Link 
              to={isReadOnly ? `/public/${targetUserId}/expenses` : "/expenses"} 
              className="text-sm text-primary font-semibold hover:underline"
            >
              All Expenses →
            </Link>
          </div>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-1.5 font-medium text-muted-foreground">
                        Description
                      </th>
                      <th className="text-right py-1.5 font-medium text-muted-foreground">
                        Amount
                      </th>
                      <th className="text-left py-1.5 font-medium text-muted-foreground pl-4">
                        Paid by
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {unsettledExpenses.map((exp) => (
                      <tr key={exp.id} className="border-b border-border/50 last:border-0">
                        <td className="py-2">
                          <p className="font-medium">{exp.description || 'Expense'}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {exp.participants?.map(p => p.player?.name).filter(Boolean).join(', ')}
                          </p>
                        </td>
                        <td className="py-2 text-right font-mono font-bold text-destructive">
                          {formatCurrency(exp.amount)}
                        </td>
                        <td className="py-2 text-left pl-4">
                          <span className="text-xs bg-muted px-2 py-0.5 rounded-full">
                            {exp.payer?.name}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-bold border-t-2 border-border/50">
                      <td className="py-2 text-left">Total Unsettled</td>
                      <td className="py-2 text-right font-mono">
                        {formatCurrency(unsettledExpenses.reduce((sum, e) => sum + Number(e.amount), 0))}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {!loading && allTimeStats.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold tracking-tight">Lifetime Standings</h2>
            <Link to={isReadOnly ? `/public/${targetUserId}/statistics` : "/statistics"}>
              <Button variant="ghost" size="sm" className="text-xs gap-1">
                <TrendingUp className="h-3 w-3" />
                Full Stats
              </Button>
            </Link>
          </div>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left py-2 px-4 font-medium text-muted-foreground">Player</th>
                      <th className="text-right py-2 px-4 font-medium text-muted-foreground">Sess</th>
                      <th className="text-right py-2 px-4 font-medium text-muted-foreground">Spent</th>
                      <th className="text-right py-2 px-4 font-medium text-muted-foreground">Net</th>
                      <th className="text-right py-2 px-4 font-medium text-muted-foreground text-[10px]">Tier</th>
                    </tr>
                    </thead>
                    <tbody>
                    {allTimeStats.slice(0, 5).map((p, i) => (
                      <tr
                        key={p.id}
                        className="border-b border-border/50 last:border-0 hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => handlePlayerClick(p.id)}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <PlayerAvatar name={p.name} avatarUrl={p.avatar_url} size={28} />
                            <div className="font-bold text-sm">{p.name}</div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-xs text-muted-foreground">
                          {p.totalSessions}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-xs text-muted-foreground">
                          {formatCurrency(p.totalSpent)}
                        </td>
                        <td className={cn(
                          'py-3 px-4 text-right font-mono font-bold',
                          p.totalNet > 0 ? 'text-success' : p.totalNet < 0 ? 'text-destructive' : 'text-muted-foreground'
                        )}>
                          {p.totalNet >= 0 ? '+' : ''}
                          <CountUp end={p.totalNet} formatter={formatCurrency} />
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded border", 
                            p.rankColor.replace('text-', 'border-').replace('text-', 'bg-').concat('/10 '),
                            p.rankColor
                          )}>
                            {p.rank}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {allTimeStats.length > 5 && (
                   <Link to={isReadOnly ? `/public/${targetUserId}/statistics` : "/statistics"}>
                     <Button variant="ghost" className="w-full text-xs text-muted-foreground h-10 rounded-none border-t border-border">
                       View all {allTimeStats.length} players
                     </Button>
                   </Link>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}